import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { services } from '@cwos/api-client';
import { Pill, SkeletonSet, SuccessEmpty, TableCard, type Column } from '@cwos/ui-admin';
import { usePermissions } from '../permissions.js';
import { SettingsCard, SettingsField, SettingsHeader } from './common.js';
import { LoadFailure } from '../LoadFailure.js';
import { AuthorizationCeilings } from './AuthorizationCeilings.js';

type SType = services.ServiceType;
type STarget = services.ServiceTarget;

const EMPTY_DRAFT = { code: '', name: '', billable: true };
const EMPTY_EDIT = {
  code: '',
  name: '',
  description: '',
  billable: false,
  defaultMinutes: '',
  active: true,
};

/**
 * Services setup (doc 01 A6 Services, doc 13 T5). Service-type catalog + targets.
 * Targets are recommendations, never authorizations (fixed framing).
 *
 * Defining a service type is the create flow here — a new organization has to
 * name the things it does (transportation, a home visit, a group session…)
 * before anything can be scheduled or documented against them. Targets are
 * per-client and are set from a client's own record, not from this catalog
 * screen.
 */
export function ServicesSettings() {
  const qc = useQueryClient();
  const { has } = usePermissions();
  const canManage = has('service:manage');

  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const [createError, setCreateError] = useState<string | null>(null);

  const types = useQuery({ queryKey: ['services', 'types'], queryFn: () => services.types() });
  const targets = useQuery({
    queryKey: ['services', 'targets'],
    queryFn: () => services.targets(),
  });

  const create = useMutation({
    mutationFn: () =>
      services.createType({
        code: draft.code.trim(),
        name: draft.name.trim(),
        billable: draft.billable,
      }),
    onSuccess: () => {
      setCreating(false);
      setDraft(EMPTY_DRAFT);
      setCreateError(null);
      void qc.invalidateQueries({ queryKey: ['services', 'types'] });
    },
    onError: (err: unknown) => {
      setCreateError(err instanceof Error ? err.message : 'We couldn’t create that service.');
    },
  });

  const canSubmit = draft.code.trim().length > 0 && draft.name.trim().length > 0;

  // EDIT — every row, imported or not. PATCH /services/types/:id is the same
  // write path the backend has always offered; a connector never updates an
  // existing type, so what is saved here stays saved across syncs. Only an
  // imported type's CODE is read-only: the connector finds the type by it.
  const [editing, setEditing] = useState<SType | null>(null);
  const [edit, setEdit] = useState(EMPTY_EDIT);
  const [editError, setEditError] = useState<string | null>(null);
  const startEdit = (r: SType) => {
    setEditing(r);
    setEditError(null);
    setEdit({
      code: r.code,
      name: r.name,
      description: r.description ?? '',
      billable: r.billable,
      defaultMinutes: r.defaultMinutes === null ? '' : String(r.defaultMinutes),
      active: r.active,
    });
  };
  const save = useMutation({
    mutationFn: () =>
      services.updateType(editing!.id, {
        // An imported type's code is how the connector finds it; never sent.
        ...(editing!.importedFromConnector ? {} : { code: edit.code.trim() }),
        name: edit.name.trim(),
        description: edit.description.trim() === '' ? null : edit.description.trim(),
        billable: edit.billable,
        defaultMinutes: edit.defaultMinutes.trim() === '' ? null : Number(edit.defaultMinutes),
        active: edit.active,
      }),
    onSuccess: () => {
      setEditing(null);
      setEditError(null);
      void qc.invalidateQueries({ queryKey: ['services', 'types'] });
    },
    onError: (err: unknown) => {
      setEditError(err instanceof Error ? err.message : 'We couldn’t save that service.');
    },
  });
  const minutesOk =
    edit.defaultMinutes.trim() === '' ||
    (Number.isInteger(Number(edit.defaultMinutes)) && Number(edit.defaultMinutes) >= 0);
  const canSave = edit.code.trim().length > 0 && edit.name.trim().length > 0 && minutesOk;

  const typeColumns: Column<SType>[] = [
    {
      key: 'name',
      header: 'Service',
      render: (r) => (
        <span style={{ fontWeight: 600 }}>
          {r.name}
          {r.importedFromConnector ? (
            <>
              {' '}
              <Pill variant="plain">Imported from connector</Pill>
            </>
          ) : null}
        </span>
      ),
    },
    { key: 'code', header: 'Code', render: (r) => r.code },
    {
      key: 'billable',
      header: 'Billable',
      render: (r) => (r.billable ? 'Billable' : 'Non-billable'),
    },
    {
      key: 'active',
      header: 'Status',
      render: (r) => (
        <Pill variant={r.active ? 'good' : 'plain'} dot>
          {r.active ? 'Active' : 'Inactive'}
        </Pill>
      ),
    },
    ...(canManage
      ? [
          {
            key: 'edit',
            header: '',
            render: (r: SType) => (
              <button
                type="button"
                className="rn-btn-quiet"
                aria-label={`Edit ${r.name}`}
                onClick={() => startEdit(r)}
              >
                Edit
              </button>
            ),
          } satisfies Column<SType>,
        ]
      : []),
  ];

  const targetColumns: Column<STarget>[] = [
    { key: 'period', header: 'Period', render: (r) => r.period },
    {
      key: 'expected',
      header: 'Expected',
      render: (r) =>
        r.expectedUnitsMax !== null
          ? `${r.expectedUnitsMin}–${r.expectedUnitsMax} ${r.unit}`
          : `${r.expectedUnitsMin} ${r.unit}`,
    },
    {
      key: 'active',
      header: 'Status',
      render: (r) => (
        <Pill variant={r.active ? 'good' : 'plain'} dot>
          {r.active ? 'Active' : 'Inactive'}
        </Pill>
      ),
    },
  ];

  return (
    <div className="rn-col" style={{ maxWidth: 760 }}>
      <SettingsHeader
        title="Services"
        blurb="The kinds of work you deliver — transportation, a home visit, a group session. Define them here; scheduling and documentation both point back to this catalog."
      />

      <SettingsCard>
        <div className="rn-card-head">
          <h2 className="rn-card-title">Service types</h2>
          {canManage ? (
            <button
              type="button"
              className="rn-btn-quiet"
              onClick={() => {
                setCreating((c) => !c);
                setCreateError(null);
              }}
              aria-expanded={creating}
            >
              {creating ? 'Cancel' : 'Create service'}
            </button>
          ) : null}
        </div>

        {creating && canManage ? (
          <form
            className="org-create"
            onSubmit={(e) => {
              e.preventDefault();
              if (canSubmit) create.mutate();
            }}
          >
            <div className="org-create-grid">
              <SettingsField label="Name">
                <input
                  style={{ marginBottom: 0 }}
                  value={draft.name}
                  autoFocus
                  placeholder="Home visit"
                  onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                />
              </SettingsField>
              <SettingsField
                label="Code"
                hint="A short, stable identifier — used on schedules and exports."
              >
                <input
                  style={{ marginBottom: 0 }}
                  value={draft.code}
                  placeholder="HOMEVISIT"
                  onChange={(e) => setDraft({ ...draft, code: e.target.value })}
                />
              </SettingsField>
            </div>
            <label className="org-check">
              <input
                type="checkbox"
                checked={draft.billable}
                onChange={(e) => setDraft({ ...draft, billable: e.target.checked })}
              />
              <span>Billable</span>
            </label>

            {createError ? (
              <p style={{ color: 'var(--color-urgent-deep)', fontSize: 13 }} role="alert">
                {createError}
              </p>
            ) : null}

            <div className="set-actions">
              <button
                type="submit"
                className="signin-devbtn"
                style={{ width: 'auto', padding: 'var(--space-10) var(--space-16)' }}
                disabled={!canSubmit || create.isPending}
              >
                {create.isPending ? 'Creating…' : 'Create service'}
              </button>
              <button
                type="button"
                className="rn-btn-quiet"
                onClick={() => {
                  setCreating(false);
                  setDraft(EMPTY_DRAFT);
                }}
              >
                Cancel
              </button>
            </div>
          </form>
        ) : null}

        {editing && canManage ? (
          <form
            className="org-create"
            aria-label={`Edit ${editing.name}`}
            onSubmit={(e) => {
              e.preventDefault();
              if (canSave) save.mutate();
            }}
          >
            <div className="org-create-grid">
              <SettingsField label="Name">
                <input
                  style={{ marginBottom: 0 }}
                  value={edit.name}
                  autoFocus
                  onChange={(e) => setEdit({ ...edit, name: e.target.value })}
                />
              </SettingsField>
              <SettingsField
                label="Code"
                hint={
                  editing.importedFromConnector
                    ? 'Set by the connected system, which finds this type by its code.'
                    : undefined
                }
              >
                <input
                  style={{ marginBottom: 0 }}
                  value={edit.code}
                  readOnly={editing.importedFromConnector}
                  onChange={(e) => setEdit({ ...edit, code: e.target.value })}
                />
              </SettingsField>
              <SettingsField label="Description">
                <input
                  style={{ marginBottom: 0 }}
                  value={edit.description}
                  onChange={(e) => setEdit({ ...edit, description: e.target.value })}
                />
              </SettingsField>
              <SettingsField label="Default minutes">
                <input
                  style={{ marginBottom: 0 }}
                  inputMode="numeric"
                  value={edit.defaultMinutes}
                  onChange={(e) => setEdit({ ...edit, defaultMinutes: e.target.value })}
                />
              </SettingsField>
            </div>
            <label className="org-check">
              <input
                type="checkbox"
                checked={edit.billable}
                onChange={(e) => setEdit({ ...edit, billable: e.target.checked })}
              />
              <span>Billable</span>
            </label>
            <label className="org-check">
              <input
                type="checkbox"
                checked={edit.active}
                onChange={(e) => setEdit({ ...edit, active: e.target.checked })}
              />
              <span>Active</span>
            </label>
            {editError ? (
              <p style={{ color: 'var(--color-urgent-deep)', fontSize: 13 }} role="alert">
                {editError}
              </p>
            ) : null}
            <div className="set-actions">
              <button
                type="submit"
                className="signin-devbtn"
                style={{ width: 'auto', padding: 'var(--space-10) var(--space-16)' }}
                disabled={!canSave || save.isPending}
              >
                {save.isPending ? 'Saving…' : 'Save changes'}
              </button>
              <button type="button" className="rn-btn-quiet" onClick={() => setEditing(null)}>
                Cancel
              </button>
            </div>
          </form>
        ) : null}

        {types.isLoading ? (
          <SkeletonSet surface="table" rows={4} />
        ) : types.isError && !types.data ? (
          <LoadFailure
            error={types.error}
            message="We couldn’t load service types just now."
            onRetry={() => void types.refetch()}
          />
        ) : (types.data ?? []).length === 0 ? (
          <SuccessEmpty
            heading="No service types yet"
            message={
              canManage
                ? 'A service type names one kind of work you deliver, like a home visit or a group session. Create one to start scheduling and documenting against it.'
                : 'A service type names one kind of work this organization delivers. An administrator with service:manage sets these up.'
            }
          />
        ) : (
          <TableCard columns={typeColumns} rows={types.data ?? []} rowKey={(r: SType) => r.id} />
        )}
      </SettingsCard>

      <SettingsCard>
        <h2 className="rn-card-title">Targets</h2>
        <p className="rn-muted" style={{ marginTop: 'var(--space-6)' }}>
          Recommended amounts — recommendations, never authorizations.
        </p>
        {targets.isLoading ? (
          <SkeletonSet surface="table" rows={3} />
        ) : targets.isError && !targets.data ? (
          <LoadFailure
            error={targets.error}
            message="We couldn’t load targets just now."
            onRetry={() => void targets.refetch()}
          />
        ) : (targets.data ?? []).length === 0 ? (
          <SuccessEmpty heading="No targets" message="Targets appear here once set for clients." />
        ) : (
          <TableCard
            columns={targetColumns}
            rows={targets.data ?? []}
            rowKey={(r: STarget) => r.id}
          />
        )}
      </SettingsCard>

      {/*
        THE OTHER HALF OF THE SENTENCE ABOVE.

        "Recommendations, never authorizations" has been on the Targets card
        since it shipped, and the product had no authorizations screen at all —
        so the distinction was asserted next to only one of the two quantities.
        The ceiling now sits beside the expectation, on the key that governs it
        (`service:manage`), which is where a reader can be told what each one is
        for without being invited to average them.
      */}
      <AuthorizationCeilings />
    </div>
  );
}
